import React, { useState, useEffect } from 'react';
import './DocumentsSection.css';

const API_BASE = '';

function DocumentsSection({ isAdmin, authToken }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newDoc, setNewDoc] = useState({ title: '', file: null });
  const [editForm, setEditForm] = useState({ title: '', file: null });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/documents`);
      const data = await response.json();
      // Sort by order field if it exists, otherwise by id
      const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setDocuments(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newDoc.title || !newDoc.file) {
      alert('Please provide a title and select a file');
      return;
    }

    try {
      // Upload the file first
      const formData = new FormData();
      formData.append('file', newDoc.file);
      
      const uploadResponse = await fetch(`${API_BASE}/api/admin/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });
      
      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        const documentUrl = `/pdfs/${uploadData.filename}`;
        
        // Now create the document entry
        const response = await fetch(`${API_BASE}/api/admin/documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: newDoc.title,
            url: documentUrl,
            order: documents.length
          })
        });

        if (response.ok) {
          setNewDoc({ title: '', file: null });
          setShowAddForm(false);
          loadDocuments();
        } else {
          alert('Failed to add document');
        }
      } else {
        alert('Failed to upload file');
      }
    } catch (error) {
      alert('Error adding document: ' + error.message);
    }
  };

  const handleUpdate = async (id) => {
    if (!editForm.title) {
      alert('Please provide a title');
      return;
    }

    try {
      let documentUrl = null;
      
      // If a new file was selected, upload it first
      if (editForm.file) {
        const formData = new FormData();
        formData.append('file', editForm.file);
        
        const uploadResponse = await fetch(`${API_BASE}/api/admin/upload-document`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${authToken}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          documentUrl = `/pdfs/${uploadData.filename}`;
        } else {
          alert('Failed to upload file');
          return;
        }
      }
      
      // Find the current document to preserve its URL if no new file
      const currentDoc = documents.find(d => d.id === id);
      
      // Update the document entry
      const response = await fetch(`${API_BASE}/api/admin/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editForm.title,
          url: documentUrl || currentDoc.url || currentDoc.pdf_url
        })
      });

      if (response.ok) {
        setEditingId(null);
        setEditForm({ title: '', file: null });
        loadDocuments();
      } else {
        alert('Failed to update document');
      }
    } catch (error) {
      alert('Error updating document: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/documents/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      });

      if (response.ok) {
        loadDocuments();
      } else {
        alert('Failed to delete document');
      }
    } catch (error) {
      alert('Error deleting document: ' + error.message);
    }
  };

  const handleMove = async (index, direction) => {
    const newDocs = [...documents];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= documents.length) return;
    
    // Swap the documents
    [newDocs[index], newDocs[newIndex]] = [newDocs[newIndex], newDocs[index]];
    
    // Update order values
    newDocs.forEach((doc, idx) => {
      doc.order = idx;
    });
    
    setDocuments(newDocs);

    // Save the new order to backend
    try {
      // Update both documents with their new order values
      await Promise.all([
        fetch(`${API_BASE}/api/admin/documents/${newDocs[index].id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...newDocs[index], order: index })
        }),
        fetch(`${API_BASE}/api/admin/documents/${newDocs[newIndex].id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...newDocs[newIndex], order: newIndex })
        })
      ]);
    } catch (error) {
      console.error('Failed to save order:', error);
      loadDocuments(); // Reload on error
    }
  };

  return (
    <div className="documents-section">
      <div className="documents-header">
        <h2>Forms, Codes & Ordinances</h2>
        {isAdmin && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : '+ Add Document'}
          </button>
        )}
      </div>

      {/* Add Form */}
      {isAdmin && showAddForm && (
        <div className="add-document-form" style={{
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3>Add New Document</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px'}}>
            <input
              type="text"
              placeholder="Document Title (e.g., 'Building Permit Application')"
              value={newDoc.title}
              onChange={(e) => setNewDoc({...newDoc, title: e.target.value})}
              style={{padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '16px'}}
            />
            <label style={{
              display: 'block',
              padding: '20px',
              border: '2px dashed #c45b1f',
              borderRadius: '8px',
              textAlign: 'center',
              cursor: 'pointer',
              background: '#fff',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
            >
              📄 Choose PDF/Document File
              <input
                type="file"
                accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.csv"
                onChange={(e) => setNewDoc({...newDoc, file: e.target.files[0]})}
                style={{display: 'none'}}
              />
              {newDoc.file && (
                <div style={{marginTop: '10px', fontSize: '14px', color: '#666', fontWeight: 'normal'}}>
                  ✅ Selected: {newDoc.file.name}
                </div>
              )}
            </label>
            <button 
              className="btn btn-success"
              onClick={handleAdd}
              disabled={!newDoc.title || !newDoc.file}
              style={{padding: '10px', fontSize: '16px'}}
            >
              Upload Document
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">Loading documents...</div>
      ) : (
        <div className="documents-list" style={{marginTop: '20px'}}>
          {documents.length === 0 ? (
            <p style={{color: '#666', textAlign: 'center', padding: '40px'}}>
              No documents added yet. {isAdmin && 'Click "Add Document" to get started.'}
            </p>
          ) : (
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {documents.map((doc, index) => (
                <li key={doc.id} style={{
                  padding: '15px 20px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.2s',
                  background: editingId === doc.id ? '#f8f9fa' : 'white'
                }}>
                  {editingId === doc.id ? (
                    // Edit mode
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '10px'}}>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                        style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '16px'}}
                        placeholder="Document title"
                      />
                      <label style={{
                        display: 'block',
                        padding: '10px',
                        border: '2px dashed #c45b1f',
                        borderRadius: '4px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '14px',
                        background: '#fff'
                      }}>
                        📄 Replace Document (Optional)
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.csv"
                          onChange={(e) => setEditForm({...editForm, file: e.target.files[0]})}
                          style={{display: 'none'}}
                        />
                        {editForm.file ? (
                          <div style={{marginTop: '5px', fontSize: '12px', color: '#666'}}>
                            ✅ New file selected: {editForm.file.name}
                          </div>
                        ) : (
                          <div style={{marginTop: '5px', fontSize: '12px', color: '#999'}}>
                            Current file will be kept if not replaced
                          </div>
                        )}
                      </label>
                      <div style={{display: 'flex', gap: '10px'}}>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleUpdate(doc.id)}
                        >
                          Save Changes
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <>
                      <div style={{flex: 1}}>
                        {(doc.url || doc.pdf_url) ? (
                          <a 
                            href={doc.url || doc.pdf_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              color: '#2d3748',
                              textDecoration: 'none',
                              fontSize: '16px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#c45b1f'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#2d3748'}
                          >
                            <span>📄</span>
                            {doc.title}
                            <span style={{fontSize: '12px', color: '#666'}}>↗</span>
                          </a>
                        ) : (
                          <div
                            style={{
                              color: '#999',
                              fontSize: '16px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <span>📄</span>
                            {doc.title}
                            <span style={{fontSize: '12px', color: '#bbb'}}>(No file uploaded)</span>
                          </div>
                        )}
                      </div>
                      {isAdmin && (
                        <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                          {/* Move buttons */}
                          <button
                            className="btn btn-sm"
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                            style={{padding: '4px 8px', opacity: index === 0 ? 0.5 : 1}}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => handleMove(index, 1)}
                            disabled={index === documents.length - 1}
                            style={{padding: '4px 8px', opacity: index === documents.length - 1 ? 0.5 : 1}}
                            title="Move down"
                          >
                            ↓
                          </button>
                          {/* Edit button */}
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setEditingId(doc.id);
                              setEditForm({
                                title: doc.title,
                                file: null
                              });
                            }}
                            style={{padding: '4px 12px'}}
                          >
                            Edit
                          </button>
                          {/* Delete button */}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(doc.id)}
                            style={{padding: '4px 12px'}}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default DocumentsSection;